import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDueAlerts, money } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { CalendarClock, AlertOctagon, Loader2, CheckCircle2, ArrowUpRight } from "lucide-react";

const Row = ({ inv, onOpen }) => {
  const overdue = inv.days < 0;
  const when = overdue ? `${Math.abs(inv.days)}d overdue` : inv.days === 0 ? "due today" : `due in ${inv.days}d`;
  return (
    <button
      data-testid={`due-row-${inv.id}`}
      onClick={() => onOpen(inv.id)}
      className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-muted/40 transition-colors"
    >
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{inv.supplier || "Unknown"}</div>
        <div className="text-xs text-muted-foreground font-mono">{inv.invoice_number} · {inv.due_date}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-sm font-semibold">{money(inv.total, inv.currency)}</div>
        <div className={cn("text-[11px] font-medium", overdue ? "text-red-500" : inv.days <= 2 ? "text-amber-500" : "text-muted-foreground")}>
          {when}{inv.exported ? " · sent to ERP" : ""}
        </div>
      </div>
    </button>
  );
};

const Column = ({ title, icon: Icon, tone, items, value, testId, emptyText, onOpen }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={testId}>
    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
      <div className={cn("flex items-center gap-2 text-sm font-medium", tone)}>
        <Icon className="h-4 w-4" /> {title}
        <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-mono text-foreground" data-testid={`${testId}-count`}>{items.length}</span>
      </div>
      <span className="font-mono text-xs text-muted-foreground">{money(value)}</span>
    </div>
    {items.length === 0 ? (
      <div className="px-4 py-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> {emptyText}
      </div>
    ) : (
      <div className="divide-y divide-border max-h-[260px] overflow-auto d20-scroll">
        {items.map((inv) => <Row key={inv.id} inv={inv} onOpen={onOpen} />)}
      </div>
    )}
  </div>
);

export const DueAlerts = ({ onLoaded }) => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  useEffect(() => {
    getDueAlerts(7).then((d) => { setData(d); onLoadedRef.current?.(d); });
  }, []);

  if (!data) {
    return (
      <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
    );
  }

  return (
    <div data-testid="due-alerts">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
          Payment Deadlines
          {data.overdue.length > 0 && (
            <span className="rounded-md bg-red-500/15 text-red-500 px-2 py-0.5 text-xs font-medium" data-testid="overdue-pill">
              {data.overdue.length} overdue
            </span>
          )}
          {data.paid_count > 0 && (
            <span className="rounded-md bg-emerald-500/15 text-emerald-500 px-2 py-0.5 text-xs font-medium" data-testid="paid-pill">
              {data.paid_count} paid · {money(data.paid_value)}
            </span>
          )}
        </h2>
        <button data-testid="view-all-due" onClick={() => navigate("/review?filter=all")} className="text-sm text-primary flex items-center gap-1 hover:gap-2 transition-all">
          All invoices <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Column title="Overdue" icon={AlertOctagon} tone="text-red-500" items={data.overdue} value={data.overdue_value}
                testId="overdue-column" emptyText="Nothing overdue — nice." onOpen={(id) => navigate(`/invoice/${id}`)} />
        <Column title={`Due in the next ${data.window_days} days`} icon={CalendarClock} tone="text-amber-500" items={data.due_soon}
                value={data.due_soon_value} testId="due-soon-column" emptyText="No payments coming up this week." onOpen={(id) => navigate(`/invoice/${id}`)} />
      </div>
    </div>
  );
};
